import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usersService } from '@/services/usersService'
import { UserModel } from '@/models/user.model'

const userKeys = {
  all: ['users'] as const,
  detail: (userId: string) => [...userKeys.all, 'detail', userId] as const,
  many: (userIds: string[]) => [...userKeys.all, 'many', [...userIds].sort()] as const,
}

export function useUser(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? userKeys.detail(userId) : userKeys.all,
    queryFn: () => (userId ? usersService.getUserById(userId) : Promise.reject(new Error('User ID is required'))),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useUsersByIds(userIds: string[] | undefined) {
  const sanitizedIds = useMemo(
    () => Array.from(new Set(userIds ?? [])).filter(Boolean),
    [userIds?.join(',')]
  )

  return useQuery({
    queryKey: userKeys.many(sanitizedIds),
    queryFn: async (): Promise<Record<string, UserModel>> => {
      if (sanitizedIds.length === 0) return {}
      const users = await Promise.all(sanitizedIds.map((id) => usersService.getUserById(id)))
      return users.reduce<Record<string, UserModel>>((acc, user) => {
        if (user.id) {
          acc[user.id] = user
        }
        return acc
      }, {})
    },
    enabled: sanitizedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}
